import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CarsWidgetFormType } from '../models/cars-widget-form.interfaces';

export interface CarsFormErrors {
  locationError: string;
  pickupDateError: string;
  pickupTimeError: string;
  dropoffDateError: string;
  dropoffTimeError: string;
}

@Injectable({
  providedIn: 'root'
})
export class CarsErrorHandlingService {
  processErrors(
    errors: CarsFormErrors,
    form: FormGroup<CarsWidgetFormType>,
    hostElement?: HTMLElement
  ): {
    locationErrorMessage: string;
    pickupDateErrorMessage: string;
    pickupTimeErrorMessage: string;
    dropoffDateErrorMessage: string;
    dropoffTimeErrorMessage: string;
    errorCount: number;
    firstErrorElement: HTMLElement | null;
  } {
    return {
      locationErrorMessage: errors.locationError,
      pickupDateErrorMessage: errors.pickupDateError,
      pickupTimeErrorMessage: errors.pickupTimeError,
      dropoffDateErrorMessage: errors.dropoffDateError,
      dropoffTimeErrorMessage: errors.dropoffTimeError,
      errorCount: this.countErrors(errors),
      firstErrorElement: this.getFirstInvalidElement(form, hostElement, errors)
    };
  }

  private countErrors(errors: CarsFormErrors): number {
    return [
      errors.locationError,
      errors.pickupDateError,
      errors.pickupTimeError,
      errors.dropoffDateError,
      errors.dropoffTimeError
    ].filter((error) => error && error.trim() !== '').length;
  }

  getFirstInvalidElement(
    form: FormGroup<CarsWidgetFormType>,
    hostElement?: HTMLElement,
    errors?: CarsFormErrors
  ): HTMLElement | null {
    return this.findInvalidControlElement(form, hostElement, errors);
  }

  private findInvalidControlElement(
    form: FormGroup<CarsWidgetFormType>,
    hostElement?: HTMLElement,
    errors?: CarsFormErrors
  ): HTMLElement | null {
    const controls = form.controls;
    const controlMap = [
      {
        check: () => controls.locationCode?.invalid || !!errors?.locationError,
        selector: '#location-field'
      },
      {
        check: () => controls.pickUpDate?.invalid || !!errors?.pickupDateError,
        selector: '#pickup-date-trigger'
      },
      {
        check: () => controls.pickUpTime?.invalid || !!errors?.pickupTimeError,
        selector: '#pickup-time-dropdown-selector button.dropdown'
      },
      {
        check: () =>
          controls.dropOffDate?.invalid || !!errors?.dropoffDateError,
        selector: '#dropoff-date-trigger'
      },
      {
        check: () =>
          controls.dropOffTime?.invalid || !!errors?.dropoffTimeError,
        selector: '#dropoff-time-dropdown-selector button.dropdown'
      }
    ];

    const invalidControl = controlMap.find((item) => item.check());
    if (!invalidControl) return null;

    const root = hostElement || document;
    return root.querySelector(invalidControl.selector) as HTMLElement;
  }
}
